"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { ArrowRight, Play, X } from "lucide-react";
import { STATS } from "@/lib/constants";

const stats = [
  STATS.activeUsers,
  STATS.languages,
  STATS.uptimeSla,
];

const demos = [
  { id: "danube", label: "Danube", src: "/videos/danube_live_demo.mp4" },
  { id: "alfies-desktop", label: "Alfies Desktop", src: "/videos/supermarket_laptop_demo.mp4" },
  { id: "alfies-mobile", label: "Alfies Mobile", src: "/videos/supermarket_phone_demo.mp4" },
];

export default function Hero() {
  const [showVideo, setShowVideo] = useState(false);
  const [activeDemo, setActiveDemo] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const touchStartY = useRef<number | null>(null);
  const [swipeOffset, setSwipeOffset] = useState(0);

  useEffect(() => {
    if (showVideo && videoRef.current) {
      document.body.style.overflow = "hidden";
      videoRef.current.src = demos[activeDemo].src;
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    } else {
      document.body.style.overflow = "";
      setSwipeOffset(0);
    }
    return () => { document.body.style.overflow = ""; };
  }, [showVideo, activeDemo]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowVideo(false);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  return (
    <>
    {/* Video Modal */}
    {showVideo && (
      <div
        className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center p-4 pt-16"
        onClick={() => setShowVideo(false)}
        onTouchStart={(e) => {
          touchStartY.current = e.touches[0].clientY;
        }}
        onTouchMove={(e) => {
          if (touchStartY.current === null) return;
          const dy = e.touches[0].clientY - touchStartY.current;
          setSwipeOffset(dy);
        }}
        onTouchEnd={() => {
          if (Math.abs(swipeOffset) > 80) {
            setShowVideo(false);
          }
          setSwipeOffset(0);
          touchStartY.current = null;
        }}
      >
        <button
          onClick={() => setShowVideo(false)}
          className="absolute top-3 right-3 sm:top-6 sm:right-6 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-black/60 text-white/70 hover:text-white hover:bg-black/80 transition-colors"
          aria-label="Close video"
        >
          <X className="w-6 h-6" />
        </button>
        <div
          className="w-full max-w-4xl max-h-[calc(100vh-7rem)]"
          style={{
            transform: `translateY(${swipeOffset}px)`,
            opacity: 1 - Math.min(Math.abs(swipeOffset) / 200, 0.5),
            transition: swipeOffset === 0 ? "transform 0.2s, opacity 0.2s" : "none",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <video
            ref={videoRef}
            controls
            playsInline
            className="w-full max-h-[calc(100vh-7rem)] rounded-2xl object-contain"
          />
        </div>
        {/* Demo Tabs */}
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[101] flex gap-1 bg-black/70 backdrop-blur-sm rounded-full p-1"
          onClick={(e) => e.stopPropagation()}
        >
          {demos.map((demo, i) => (
            <button
              key={demo.id}
              onClick={() => setActiveDemo(i)}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                activeDemo === i
                  ? "bg-accent text-bg"
                  : "text-white/70 hover:text-white"
              }`}
            >
              {demo.label}
            </button>
          ))}
        </div>
      </div>
    )}

    <section className="relative min-h-screen flex items-center pt-20 bg-bg">
      <div className="absolute inset-0 bg-gradient-mesh" />

      <div className="container relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          {/* Badge */}
          <p className="text-sm text-fg-subtle uppercase tracking-wider mb-8">
            Trusted by Danube, Alfies & leading retailers
          </p>

          {/* Headline */}
          <h1 className="text-hero text-fg mb-6">
            Conversational AI that{" "}
            <span className="text-accent">sells</span>
          </h1>

          {/* Subtitle */}
          <p className="text-body-large max-w-2xl mx-auto mb-10">
            AI sales agents that guide your customers from first question to checkout,
            increasing order values, conversion rates, and loyalty.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <button
              onClick={() => setShowVideo(true)}
              className="btn-primary text-base px-8 py-4 group"
            >
              <Play className="w-4 h-4" fill="currentColor" />
              Watch Live Demo
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
            <Link
              href="/contact"
              className="btn-secondary text-base px-8 py-4"
            >
              Book a Demo
            </Link>
          </div>

          {/* Social proof */}
          <p className="text-sm text-fg-subtle mb-8">
            Typical setup: 2 weeks · We respond within 24 hours
          </p>

          {/* Trust Badges */}
          <div className="flex items-center justify-center flex-wrap gap-x-8 gap-y-2 mb-16 text-sm text-fg-subtle">
            <span>SOC 2 Aligned</span>
            <span className="w-px h-4 bg-[var(--border-subtle)] hidden sm:block" />
            <span>GDPR Compliant</span>
            <span className="w-px h-4 bg-[var(--border-subtle)] hidden sm:block" />
            <span>Enterprise Ready</span>
          </div>

          {/* Demo Preview Card */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-bg-elevated border border-[var(--border-subtle)] rounded-2xl overflow-hidden">
              {/* Browser Chrome */}
              <div className="flex items-center gap-2 px-4 py-3 bg-bg-card border-b border-[var(--border-subtle)]">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-error" />
                  <div className="w-3 h-3 rounded-full bg-warn" />
                  <div className="w-3 h-3 rounded-full bg-success" />
                </div>
                <div className="flex-1 text-center">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-bg rounded-md text-xs text-fg-subtle">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    isartech.io/agent
                  </div>
                </div>
              </div>

              {/* Chat Interface Mockup */}
              <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                {/* User Message */}
                <div className="flex gap-2 sm:gap-3 justify-end">
                  <div className="flex-1 max-w-[85%] sm:max-w-[70%]">
                    <div className="bg-accent rounded-2xl rounded-tr-sm p-3 sm:p-4">
                      <p className="text-sm sm:text-[15px] text-bg">
                        I&apos;m hosting a BBQ this weekend, need everything for 8 people
                      </p>
                    </div>
                  </div>
                </div>

                {/* Agent: Tool Call */}
                <div className="flex gap-2 sm:gap-3">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                    <span className="text-bg text-[10px] sm:text-xs font-bold">AI</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-bg-card border border-[var(--border-subtle)] rounded-lg">
                        <span className="w-1.5 h-1.5 bg-success rounded-full" />
                        <span className="text-[10px] sm:text-xs text-fg-muted font-mono">search_products</span>
                      </div>
                      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-bg-card border border-[var(--border-subtle)] rounded-lg">
                        <span className="w-1.5 h-1.5 bg-success rounded-full" />
                        <span className="text-[10px] sm:text-xs text-fg-muted font-mono">check_inventory</span>
                      </div>
                      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-bg-card border border-[var(--border-subtle)] rounded-lg">
                        <span className="w-1.5 h-1.5 bg-success rounded-full" />
                        <span className="text-[10px] sm:text-xs text-fg-muted font-mono">add_to_cart</span>
                      </div>
                    </div>

                    <div className="bg-bg-card rounded-2xl rounded-tl-sm p-3 sm:p-4">
                      <p className="text-sm sm:text-[15px] text-fg mb-3">
                        I&apos;ve put together a complete BBQ set for 8. Everything&apos;s in stock at your nearest store:
                      </p>
                      <div className="grid grid-cols-3 gap-1.5 sm:gap-2 mb-3">
                        {[
                          { name: "Chicken Burgers 8pc", price: "€12.99", img: "https://d1c124wpoew66.cloudfront.net/spree/images/attachments/000/089/478/product/503371216229.jpg" },
                          { name: "Charcoal Sticks", price: "€8.49", img: "https://d1c124wpoew66.cloudfront.net/spree/images/attachments/000/019/441/product/628101346120.jpg" },
                          { name: "Sesame Burger Buns", price: "€3.29", img: "https://d1c124wpoew66.cloudfront.net/spree/images/attachments/000/069/374/product/6281100171447_%28144%29.jpg" },
                        ].map((item) => (
                          <div key={item.name} className="bg-bg rounded-lg p-1.5 sm:p-2">
                            <div className="aspect-square rounded-md mb-1.5 sm:mb-2 overflow-hidden relative">
                              <Image src={item.img} alt={item.name} fill className="object-cover" unoptimized />
                            </div>
                            <div className="text-[10px] sm:text-xs text-fg truncate">{item.name}</div>
                            <div className="text-[10px] sm:text-xs text-accent font-medium">{item.price}</div>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-accent/10 border border-accent/20 rounded-lg">
                        <span className="w-1.5 h-1.5 bg-accent rounded-full" />
                        <span className="text-[10px] sm:text-xs text-accent">Added 6 items to cart · €47.26 total</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Follow-up */}
                <div className="flex gap-2 sm:gap-3 justify-end">
                  <div className="flex-1 max-w-[85%] sm:max-w-[70%]">
                    <div className="bg-accent rounded-2xl rounded-tr-sm p-3 sm:p-4">
                      <p className="text-sm sm:text-[15px] text-bg">
                        Swap the beef for something vegetarian, two of the guests don&apos;t eat meat
                      </p>
                    </div>
                  </div>
                </div>

                {/* Agent responds */}
                <div className="flex gap-2 sm:gap-3">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                    <span className="text-bg text-[10px] sm:text-xs font-bold">AI</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-bg-card border border-[var(--border-subtle)] rounded-lg">
                        <span className="w-1.5 h-1.5 bg-success rounded-full" />
                        <span className="text-[10px] sm:text-xs text-fg-muted font-mono">search_products</span>
                      </div>
                      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-bg-card border border-[var(--border-subtle)] rounded-lg">
                        <span className="w-1.5 h-1.5 bg-success rounded-full" />
                        <span className="text-[10px] sm:text-xs text-fg-muted font-mono">update_cart</span>
                      </div>
                    </div>
                    <div className="bg-bg-card rounded-2xl rounded-tl-sm p-3 sm:p-4">
                      <p className="text-sm sm:text-[15px] text-fg">
                        Done! I&apos;ve swapped the beef burgers for halloumi skewers and veggie patties. Your cart now has 6 beef burgers for the meat-eaters and 2 vegetarian options. Still €47.26.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 sm:gap-8 max-w-2xl mx-auto mt-16">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-accent mb-1">
                  {stat.value}
                </div>
                <div className="text-xs sm:text-sm text-fg-subtle">{stat.label}</div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
    </>
  );
}
